import React from 'react';
import { cn } from '@/lib/utils';

// =============================================================================
// AdminSidebarSkeleton — FASE 3
// Fuente de verdad: docs/admin-dashboard-skeleton-roadmap/fase-0-inventario-arquitectonico.md
// Regla crítica: solo skeleton-shimmer (shimmer direccional), nunca <Skeleton> de shadcn,
// nunca animate-pulse, nunca colores de marca.
//
// Calca 1:1 la estructura de AdminSidebar.tsx:
//   - aside: fixed left-0 top-0 z-40 h-screen border-r bg-sidebar
//   - w-64 (expanded) / w-20 (collapsed) con transition-all duration-200
//   - 4 zonas: Header (h-16) + Nav (4 items) + Equipo + Digital Card
// =============================================================================

// ─── Primitivo base ──────────────────────────────────────────────────────────
function Block({ 
    className, style }: Readonly<{ 
        className?: string; 
        style?: React.CSSProperties }>
      ) {
  return (
    <div
      aria-hidden="true"
      className={cn('skeleton-shimmer rounded', className)}
      style={style}
    />
  );
}

// =============================================================================
// ZONA 1 — Header (h-16 border-b border-sidebar-border px-4)
// Expanded: logo h-8 w-32 rounded-md + toggle button size-8 rounded-full
// Collapsed: icono cuadrado size-8 centrado
// =============================================================================
function SidebarHeaderSkeleton({
     expanded }: Readonly<{ expanded: boolean }>
    ) {
  return (
    <div className="flex h-16 items-center justify-between border-b border-sidebar-border px-4">
      {expanded ? (
        <>
          {/* Logo horizontal: h-8 w-32 rounded-md */}
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Block className="h-8 w-32 rounded-md" />
          </div>
          {/* Toggle button: size-8 rounded-full */}
          <Block className="size-8 shrink-0 rounded-full" />
        </>
      ) : (
        /* Collapsed: icono centrado */
        <div className="flex w-full items-center justify-center">
          <Block className="size-8 rounded-full" />
        </div>
      )}
    </div>
  );
}

// =============================================================================
// ZONA 2 — Nav (space-y-1 p-3)
// 8 ítems de nav — calca la sensación visual completa del sidebar real.
// Nota: el código actual de ADMIN_NAV_ITEMS tiene 4 items activos, pero la
// especificación del skeleton usa 8 para un aspecto más completo en primera carga.
// Cada item: rounded-xl px-3 py-2.5 flex items-center gap-3
//   - Icon: size-5 = 20px
//   - Label: text-sm (solo en expanded) → h-3.5 flex-1 rounded
// =============================================================================

const NAV_ITEM_COUNT = 4;

const NAV_SKELETON_ITEMS = Array.from({ length: NAV_ITEM_COUNT }, (_, i) => ({
  key: `nav-item-${i}`,
  isActive: i === 0,
}));

function SidebarNavSkeleton({ 
    expanded }: Readonly<{ expanded: boolean }>
  ) {
  return (
    <nav className="space-y-1 p-3" aria-hidden="true">
      {NAV_SKELETON_ITEMS.map(({ key, isActive }) => ( 
        <div
          key={key}
          className={cn(
            'flex w-full items-center rounded-xl px-3 py-2.5',
            expanded ? 'gap-3' : 'justify-center px-2',
            // Primer item = active state — fondo ligeramente más visible
            isActive ? 'bg-sidebar-accent/60' : '',
          )}
        >
          {/* Icon: size-5 = 20px */}
          <Block className="size-5 shrink-0 rounded-md" />
          {/* Label — solo en expanded */}
          {expanded ? <Block className="h-3.5 flex-1 rounded" /> : null}
        </div>
      ))}
    </nav>
  );
}

// =============================================================================
// ZONA 3 — Equipo section (px-5 pb-5 pt-2) — solo visible en expanded
// Estructura real:
//   - Fila header: h3 "Mi Equipo" text-sm font-semibold + botón "Añadir" h-8 px-2.5 rounded-full
//   - Input búsqueda: hidden md:block mb-4 → h-9 rounded-full bg-slate-50 pl-10
//   - Lista scrollable: max-h-[256px] overflow-y-auto → 4 items tipo pill
//     Cada pill: p-1.5 pr-4 rounded-full border → avatar size-10 + 2 líneas texto
// =============================================================================
const TEAM_SKELETON_COUNT = 4; // Calca PillsSkeleton existente en TeamListSkeleton

const TEAM_SKELETON_KEYS = Array.from(
  { length: TEAM_SKELETON_COUNT },
  (_, i) => `team-pill-${i}`,
);

function SidebarTeamSkeleton() {
  return (
    <div className="px-5 pb-5 pt-2">
      {/* Header: "Mi Equipo" + botón "Añadir" */}
      <div className="mb-4 flex items-center justify-between">
        <Block className="h-4 w-20 rounded" />
        {/* Botón Añadir: micro button h-8 */}
        <Block className="h-8 w-16 rounded-full" />
      </div>

      {/* Input búsqueda: hidden md:block mb-4 → h-9 rounded-full pl-10 */}
      <div className="relative mb-4 hidden md:block">
        {/* El pl-10 del input real crea espacio para el icono de lupa */}
        <Block className="h-9 w-full rounded-full" />
      </div>

      {/* Lista scrollable: max-h-[256px] — 4 pills de agente */}
      <div className="flex max-h-[256px] flex-col gap-2.5 overflow-hidden pr-1">
        {TEAM_SKELETON_KEYS.map((key) => (
          <div
            key={key}
            className="flex shrink-0 items-center gap-3 rounded-full border border-slate-200/70 bg-white p-1.5 pr-4 dark:border-zinc-800 dark:bg-zinc-950"
          >
            {/* Avatar: size-10 rounded-full */}
            <Block className="size-10 shrink-0 rounded-full" />
            {/* Texto: nombre + rol */}
            <div className="min-w-0 flex-1 space-y-1.5">
              <Block className="h-3 w-3/4 rounded" />
              <Block className="h-2.5 w-1/2 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// ZONA 4 — Digital Card (border-t border-sidebar-border px-3 pb-4 pt-5)
// Real: DigitalCardSidebarTrigger — preview de imagen con overlay de acciones
// Solo visible en expanded cuando digitalCard.hasDigitalCard
// Para el skeleton: bloque card-shaped con shimmer, sin lógica condicional
// =============================================================================
function SidebarDigitalCardSkeleton() {
  return (
    <div className="border-t border-sidebar-border px-3 pb-4 pt-5">
      {/* Digital Card Pill: flex w-full justify-between rounded-full py-3.5 pl-6 pr-3 */}
      <div className="flex w-full items-center justify-between overflow-hidden rounded-full border border-slate-200/50 bg-slate-50 py-3.5 pl-6 pr-3 dark:border-zinc-800/50 dark:bg-zinc-900/50">
        <div className="flex flex-col gap-1.5">
          <Block className="h-3.5 w-24 rounded" />
          <Block className="h-2.5 w-20 rounded" />
        </div>
        <Block className="size-9 shrink-0 rounded-full" />
      </div>
    </div>
  );
}

// =============================================================================
// EXPORT PÚBLICO — AdminSidebarSkeleton
//
// Props:
//   expanded (boolean, default true):
//     Calca el estado expanded del sidebar real para reservar el ancho correcto
//     y mostrar/ocultar los elementos que solo aparecen en expanded.
//   showDigitalCard (boolean, default true):
//     Calca digitalCard.hasDigitalCard — muestra la zona 4 condicionalmente.
//
// Propiedades estructurales que previenen CLS:
//   - Mismo `fixed left-0 top-0 z-40 h-screen` que el sidebar real
//   - Mismo `border-r border-sidebar-border bg-sidebar`
//   - Mismo `transition-all duration-200 ease-out` — la transición del swap
//     skeleton→real no produce ningún salto de layout
// =============================================================================
export interface AdminSidebarSkeletonProps {
  /** Estado expanded — determina ancho (w-64/w-20) y qué elementos mostrar. */
  expanded?: boolean;
  /** Si true, pinta la zona de Digital Card al fondo del sidebar. */
  showDigitalCard?: boolean;
}

export function AdminSidebarSkeleton({
  expanded = true,
  showDigitalCard = true,
}: Readonly<AdminSidebarSkeletonProps>
) {
  return (
    <aside
      className={cn(
        // Calca exacto de las clases estructurales del sidebar real
        'fixed left-0 top-0 z-40 h-screen border-r border-sidebar-border bg-sidebar',
        'flex flex-col transition-all duration-200 ease-out',
        expanded ? 'w-64' : 'w-20',
      )}
      aria-label="Cargando navegación del panel"
      aria-busy="true"
    >
      <div className="flex h-full flex-col">
        {/* ZONA 1 — Header (h-16 fijo) */}
        <SidebarHeaderSkeleton expanded={expanded} />

        {/* ZONA 2+3 — Nav + Equipo (flex-1 overflow-y-auto) */}
        <div className="flex-1 overflow-y-auto">
          <SidebarNavSkeleton expanded={expanded} />

          {/* ZONA 3 — Equipo: solo en expanded */}
          {expanded ? <SidebarTeamSkeleton /> : null}
        </div>

        {/* ZONA 4 — Digital Card: solo en expanded + hasDigitalCard */}
        {expanded && showDigitalCard ? <SidebarDigitalCardSkeleton /> : null}
      </div>
    </aside>
  );
}
